import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

type PlanOffer = {
  id: string;
  plan: 'plus' | 'pro';
  message: string | null;
  created_at: string;
};

const PLAN_LABEL: Record<'plus' | 'pro', string> = {
  plus: 'Explorer',
  pro: 'Master',
};

/**
 * PlanOfferModal
 *
 * Mounted once for any signed-in user (see ProtectedRoute). On mount, checks
 * GET /api/me/plan-offer for a pending "admin is gifting you a plan" offer.
 * If one exists, blocks with a popup until the user accepts or rejects it.
 */
export default function PlanOfferModal() {
  const { session, refreshPlan } = useAuth();
  const [offer, setOffer] = useState<PlanOffer | null>(null);
  const [responding, setResponding] = useState<'accept' | 'reject' | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!session) return;
    let cancelled = false;

    fetch(`${API}/api/me/plan-offer`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled) setOffer(json?.offer ?? null);
      })
      .catch(() => {
        /* silent — a missed popup isn't worth erroring the whole app over */
      });

    return () => {
      cancelled = true;
    };
  }, [session]);

  if (!offer || !session) return null;

  const respond = async (accept: boolean) => {
    setResponding(accept ? 'accept' : 'reject');
    setError('');
    try {
      const res = await fetch(`${API}/api/me/plan-offer/${offer.id}/respond`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accept }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json?.error ?? 'Something went wrong. Please try again.');
        setResponding(null);
        return;
      }
      if (accept) await refreshPlan();
      setOffer(null);
    } catch {
      setError('Network error. Please check your connection and try again.');
      setResponding(null);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4"
    >
      <div className="w-full max-w-md rounded-2xl bg-[#FBF7F1] p-6 shadow-xl">
        <div className="w-14 h-14 rounded-full bg-[#FFE5D4] flex items-center justify-center mb-5">
          <span
            className="material-symbols-outlined text-[#ff795c]"
            style={{ fontSize: '28px', fontVariationSettings: "'FILL' 1" }}
           aria-hidden="true">
            redeem
          </span>
        </div>

        <h2 className="text-xl font-bold text-on-surface mb-2" style={{ fontFamily: "'Fraunces', serif" }}>
          You've been gifted the {PLAN_LABEL[offer.plan]} plan
        </h2>

        <p className="text-sm text-on-surface-variant leading-relaxed mb-1">
          An admin wants to give you free access to the <strong>{PLAN_LABEL[offer.plan]}</strong> plan.
        </p>

        {offer.message && (
          <p className="text-sm text-on-surface-variant italic bg-black/5 rounded-lg px-3 py-2 mt-3 mb-1">
            "{offer.message}"
          </p>
        )}

        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

        <div className="flex gap-3 mt-6">
          <button
            onClick={() => respond(false)}
            disabled={responding !== null}
            className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold border border-outline-variant/40 text-on-surface-variant hover:border-outline-variant/70 disabled:opacity-50"
          >
            {responding === 'reject' ? 'Declining…' : 'No thanks'}
          </button>
          <button
            onClick={() => respond(true)}
            disabled={responding !== null}
            className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold bg-[#286958] text-white disabled:opacity-50"
          >
            {responding === 'accept' ? 'Activating…' : 'Accept'}
          </button>
        </div>
      </div>
    </div>
  );
}
